import { AppointmentDepositCheckoutSession } from '../../types/appointments';

/**
 * Builds the line items shown in the deposit checkout pricing table.
 * Under cash discount mode, "Price" is the inflated value; feeless methods
 * (CASH/DIGITAL) get a cash-discount line and pay tax on the deflated base.
 */
export function getDepositPricingRows(
  session: AppointmentDepositCheckoutSession | undefined | null,
  paymentMethod: string,
  isLoading: boolean,
) {
  const cashDiscountEnabled = session?.cashDiscountEnabled ?? false;
  const isFeeless = paymentMethod === 'CASH' || paymentMethod === 'DIGITAL';
  const isTerminal = paymentMethod === 'TERMINAL';
  // Off-platform (cash/digital) deposits skip sales tax unless the business
  // opts in — mirrors markDepositPaid.
  const applyFeelessTax = session?.areOffPlatformDepositsTaxed ?? false;

  const inflatedPriceCents =
    (session?.depositCents ?? 0) +
    (cashDiscountEnabled ? session?.serviceFeeCents ?? 0 : 0);
  const cashDiscountCents =
    isFeeless && cashDiscountEnabled ? session?.serviceFeeCents ?? 0 : 0;
  // Cash/digital pay tax on the deflated deposit (no tax-on-fee).
  const feelessSalesTaxCents =
    (session?.feelessTotalPriceCents ?? 0) - (session?.depositCents ?? 0);
  const salesTaxCents = isFeeless
    ? applyFeelessTax
      ? feelessSalesTaxCents
      : 0
    : session?.salesTaxCents ?? 0;
  const showConvenienceFee =
    !cashDiscountEnabled && isTerminal && !!session?.serviceFeeCents;
  const convenienceFeeCents = showConvenienceFee
    ? session?.serviceFeeCents ?? 0
    : 0;
  const totalCents =
    inflatedPriceCents -
    cashDiscountCents +
    salesTaxCents +
    convenienceFeeCents;

  return {
    preSubtotalItems: [{ label: 'Price', value: inflatedPriceCents / 100 }],
    postSubtotalItems: [
      {
        label: 'Cash discount applied',
        value: -(cashDiscountCents / 100),
        visible: cashDiscountCents > 0,
      },
      {
        label: 'Sales tax',
        value: salesTaxCents / 100,
        visible: salesTaxCents > 0,
      },
      {
        label: 'Convenience fee',
        value: convenienceFeeCents / 100,
        visible: showConvenienceFee,
      },
    ],
    total: totalCents / 100,
    subtotal: inflatedPriceCents / 100,
    isLoading,
  };
}
